import _ from 'lodash';
import moment from 'moment';
import { Action, Dispatch } from 'redux';
import { IAppState } from './reducers';
import { MetadataKeys } from '../api/vault/logs';
import {
  searchAssetAccessLogs,
  AssetAccessLogKey,
  IAssetAccessLogHit,
  ILogSearchParams,
} from '../api/vault/searchEngine';

export enum ActionType {
  Toggle = 'AssetShares.Toggle',
  StartQuery = 'AssetShares.StartQuery',
  StopQuery = 'AssetShares.StopQuery',
  LoadShares = 'AssetShares.LoadShares',
  ClearShares = 'AssetShares.ClearShares',
  Reset = 'AssetShares.Reset',
}

export interface IAssetShare {
  recipient: string;
  created: string;
  expiration: string;
}

export interface IState {
  isOpen: boolean;
  isQuerying: boolean;
  logs: Array<IAssetAccessLogHit>;
  shares: Array<IAssetShare>;
}

export interface IAssetSharesAction extends Action<ActionType> {
  payload?: any;
}

const INITIAL_STATE: IState = {
  isOpen: false,
  isQuerying: false,
  logs: [],
  shares: [],
};

export default (state: IState = INITIAL_STATE, action: IAssetSharesAction) => {
  switch (action.type) {
    case ActionType.Toggle:
      return {
        ...state,
        isOpen: !state.isOpen,
      };
    case ActionType.StartQuery:
      return {
        ...state,
        isQuerying: true,
      };
    case ActionType.StopQuery:
      return {
        ...state,
        isQuerying: false,
      };
    case ActionType.LoadShares:
      return {
        ...state,
        logs: action.payload.logs,
        shares: action.payload.shares,
      };
    case ActionType.ClearShares:
      return {
        ...state,
        logs: [],
        shares: [],
      };
    case ActionType.Reset:
      return INITIAL_STATE;
    default:
      return state;
  }
};

export const toggleAssetShares = () => ({
  type: ActionType.Toggle,
});

export const clearAssetShares = () => ({
  type: ActionType.ClearShares,
});

export const resetAssetShares = () => ({
  type: ActionType.Reset,
});

export const queryAssetShareLogs = (assetId: string, toggleOnComplete: boolean = false) => async (
  dispatch: Dispatch<IAssetSharesAction>,
  getState: () => IAppState,
) => {
  dispatch({
    type: ActionType.StartQuery,
  });

  const searchParams: ILogSearchParams = {
    queryString: '',
    filters: [
      [AssetAccessLogKey.AssetId, assetId],
      [AssetAccessLogKey.ActionType, ['Shared']],
    ],
  };

  try {
    const response = await searchAssetAccessLogs(searchParams);
    const { data: { data: { hits: { hits } } } } = response;

    const logs = _.orderBy<IAssetAccessLogHit>(hits, (hit) => [moment(hit._source.actionDate).unix()], 'desc');
    const shares: Array<IAssetShare> = logs.reduce((collection, log) => {
      const { _source: { actionDate: created, metadata } } = log;
      const expiration = metadata.find(({ label }) => label === MetadataKeys.Expiration);
      const recipients = metadata.filter(({ label }) => label === MetadataKeys.Recipient);

      if (expiration && recipients.length) {
        recipients.forEach(({ value: recipient }) => collection.push({
          recipient,
          created,
          expiration: expiration.value,
        }));
      }

      return collection;
    }, []);

    dispatch({
      payload: {
        logs,
        shares,
      },
      type: ActionType.LoadShares,
    });
  } catch (e) {
    console.error(`[QA] Error occurred while querying for asset "${assetId}" logs`, e.toString());
  }

  dispatch({
    type: ActionType.StopQuery,
  });

  if (toggleOnComplete) {
    dispatch({
      type: ActionType.Toggle,
    });
  }
};
